import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import React, { useEffect, useRef } from "react";
import { Code, Pencil, Trash2 } from "lucide-react";
import { decodeEmbedHtml } from "../lib/htmlEmbed";

/**
 * `HtmlEmbed` — a TipTap block-level atom node that stores an arbitrary
 * HTML/iframe embed snippet (Facebook post, Twitter tweet, Instagram
 * widget, custom iframe, or any raw HTML) as a base64-encoded string on
 * a placeholder `<div>`. The stored body HTML looks like:
 *
 *     <div class="html-embed-placeholder"
 *          data-html-b64="…base64 of raw embed HTML…"
 *          contenteditable="false"></div>
 *
 * The rendered post page uses `hydrateHtmlEmbeds` (see lib/htmlEmbed.js)
 * to inject the decoded HTML verbatim (no sanitisation) at mount time,
 * re-executing scripts from the raw markup. The editor's node view below
 * renders the same live HTML in-place — not just a placeholder pill —
 * so what the admin sees while editing matches the published page.
 */
const HtmlEmbedNodeView = ({ node, editor, getPos, deleteNode }) => {
  const b64 = node.attrs.htmlB64 || "";
  const raw = decodeEmbedHtml(b64);
  const canEdit = editor?.isEditable !== false;
  const contentRef = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // Preserve the admin's HTML exactly as pasted — no sanitisation,
    // matches the published-view behaviour in hydrateHtmlEmbeds().
    el.innerHTML = raw;
    // Re-create <script> tags so the browser actually executes them
    // (setting innerHTML does not run inline/appended scripts).
    const oldScripts = el.querySelectorAll("script");
    oldScripts.forEach((old) => {
      const fresh = document.createElement("script");
      for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
      if (old.textContent) fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    });
  }, [raw]);

  const openEditor = () => {
    if (!canEdit || typeof getPos !== "function") return;
    const evt = new CustomEvent("html-embed-edit", {
      detail: { pos: getPos(), htmlB64: b64, width: node.attrs.width || "full" },
    });
    editor.view.dom.dispatchEvent(evt);
  };

  return (
    <NodeViewWrapper as="div" className="my-4 not-prose" data-testid="html-embed-node">
      <div className="overflow-hidden rounded-md border border-dashed border-[#0A0F17]/30 bg-white">
        <div className="flex items-center gap-2 border-b border-dashed border-[#0A0F17]/20 bg-[#F1F5F9] px-3 py-1.5">
          <Code size={14} className="shrink-0 text-[#0A0F17]" />
          <span className="label-kicker text-[#0A0F17]">HTML embed</span>
          {canEdit ? (
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                onClick={openEditor}
                className="inline-flex items-center gap-1 rounded-sm border border-black/15 bg-white px-2 py-1 text-xs font-semibold hover:border-[#0A0F17]"
                data-testid="html-embed-edit-btn"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                type="button"
                onClick={() => deleteNode?.()}
                className="inline-flex items-center gap-1 rounded-sm border border-black/15 bg-white px-2 py-1 text-xs font-semibold hover:border-[#E63946] hover:text-[#E63946]"
                data-testid="html-embed-delete-btn"
              >
                <Trash2 size={12} /> Remove
              </button>
            </div>
          ) : null}
        </div>
        {raw ? (
          <div
            ref={contentRef}
            className="p-3"
            contentEditable={false}
            data-testid="html-embed-live-preview"
          />
        ) : (
          <p className="p-3 text-xs text-[var(--brand-body)]">(empty embed)</p>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export const HtmlEmbed = Node.create({
  name: "htmlEmbed",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      htmlB64: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-html-b64") || "",
        renderHTML: (attrs) =>
          attrs.htmlB64 ? { "data-html-b64": attrs.htmlB64 } : {},
      },
      width: {
        default: "full",
        parseHTML: (el) => el.getAttribute("data-width") || "full",
        renderHTML: (attrs) =>
          attrs.width ? { "data-width": attrs.width } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.html-embed-placeholder[data-html-b64]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const widthClass = `html-embed-width-${HTMLAttributes["data-width"] || "full"}`;
    // The stored body HTML is exactly this element. On the public post
    // page `hydrateHtmlEmbeds` decodes `data-html-b64` and injects the
    // real embed markup inside this div at mount time.
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        class: `html-embed-placeholder ${widthClass}`,
        contenteditable: "false",
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HtmlEmbedNodeView);
  },
});
